import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useScope } from '../context/ScopeContext';
import { useAuth } from '../context/AuthContext';
import { Course, Tag, UserRole } from '../types';
import { scopeCounts, scopeTaggedCounts } from '../lib/catalogScope';

type ScopeCourse = Pick<Course, 'id' | 'tags'>;

interface ScopeSwitcherProps {
  /**
   * The UNSCOPED course list. Counts have to say what the student would find
   * under each value, not what survives the current scope — and the dead-end
   * rule below needs to see values the current scope has filtered away.
   */
  courses: ScopeCourse[];
  /**
   * `chrome` — a compact chip in the signed-in header, beside the profile menu.
   * `bar`    — a full-width band: the public catalogue, where there is no app
   *            chrome to sit in, and the mobile drawer, where the chip is
   *            hidden. Spacing is the caller's, so it can sit in either.
   */
  variant?: 'chrome' | 'bar';
  /**
   * Spacing for the call site. Passed in rather than baked in because the
   * component renders NOTHING when there's nothing to switch — a margin on a
   * wrapper div would leave dead space on every unscoped tenant.
   */
  className?: string;
}

/**
 * The catalogue scope switch.
 *
 * The scope applies to everything a student sees, so its control sits in the
 * chrome rather than on one page — and states the current value in words,
 * never an icon, because the whole contract with the reader is that they can
 * see what they are being shown.
 *
 * Two rules about what it offers:
 *
 *  - Hidden for instructors and admins. Staff see every course regardless of
 *    scope, so a control that changed nothing would be worse than none.
 *  - A value no course CARRIES is not offered. Selecting it could only land
 *    the student somewhere near-empty — the same reasoning the filter bar and
 *    the pivot pills apply to dead-end tags. This matters right now: the IEB
 *    courses are unpublished, so IEB must not be offered until they are, or
 *    the switch is a trapdoor. Note this gates on courses carrying the tag,
 *    not on what the value would show — untagged courses appear under every
 *    value, and counting them here would make an empty curriculum look live.
 *
 * With fewer than two live values there is nothing to switch between and the
 * control renders nothing at all.
 */
export const ScopeSwitcher: React.FC<ScopeSwitcherProps> = ({
  courses,
  variant = 'chrome',
  className = '',
}) => {
  const { user } = useAuth();
  const { axes, selection, setScopeValue } = useScope();
  const [openAxis, setOpenAxis] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openAxis) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpenAxis(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenAxis(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openAxis]);

  const live = useMemo(
    () =>
      axes.map((axis) => {
        // Two different questions. `counts` is what a student would FIND under
        // each value (untagged courses included, since those show everywhere);
        // `tagged` is whether the curriculum exists yet at all. Offering on
        // `counts` would let one untagged course make IEB offerable before a
        // single IEB course was published.
        const counts = scopeCounts(courses, axis);
        const tagged = scopeTaggedCounts(courses, axis);
        const current = selection[axis.category.id];
        // Keep the current value even at zero, so an empty scope still says
        // which one you're in and offers the way out.
        const options = axis.tags.filter((t) => (tagged[t.id] ?? 0) > 0 || t.id === current?.id);
        return { axis, counts, current, options };
      }),
    [axes, courses, selection],
  );

  const isStaff = user?.role === UserRole.INSTRUCTOR || user?.role === UserRole.ADMIN;
  const switchable = live.filter((l) => l.current && l.options.length > 1);
  if (isStaff || switchable.length === 0) return null;

  const bar = variant === 'bar';

  return (
    <div
      ref={ref}
      className={`${
        bar
          ? 'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white px-4 py-3'
          : 'flex items-center gap-3'
      } ${className}`.trim()}
    >
      {switchable.map(({ axis, counts, current, options }) => {
        const open = openAxis === axis.category.id;
        // Two or three values: show them ALL, side by side, the current one
        // filled in. A dropdown hid the other board behind an arrow nobody
        // read as "there is another board" — the first IEB learners defaulted
        // into the NSC and had no idea (Dylan, 5 Sep 2026). Where you are and
        // where you could be are both on screen, and switching is one tap.
        // More than three falls back to the list, which is what a list is for.
        if (options.length <= 3) {
          return (
            <div key={axis.category.id} className="flex items-center gap-3">
              <span
                className={`text-[10px] font-bold uppercase tracking-wider text-gray-400 ${
                  bar ? '' : 'hidden lg:inline'
                }`}
              >
                {axis.category.name}
              </span>
              <div
                role="radiogroup"
                aria-label={axis.category.name}
                className="scope-seg inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
              >
                {options.map((tag: Tag) => {
                  const active = tag.id === current!.id;
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setScopeValue(axis.category.id, tag)}
                      className={`scope-seg-btn rounded-md px-3 py-1 text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                        active
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      {tag.name}
                    </button>
                  );
                })}
              </div>
              {bar && (
                <span className="text-xs text-gray-500">
                  {counts[current!.id] ?? 0} course{(counts[current!.id] ?? 0) === 1 ? '' : 's'}
                </span>
              )}
            </div>
          );
        }
        return (
          <div key={axis.category.id} className={bar ? 'flex items-center gap-3' : 'relative'}>
            {bar && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                {axis.category.name}
              </span>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenAxis(open ? null : axis.category.id)}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  bar ? 'border-gray-200 hover:border-gray-300' : 'border-gray-100'
                }`}
              >
                {!bar && (
                  <span className="hidden lg:inline text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    {axis.category.name}
                  </span>
                )}
                <span className="text-sm font-bold text-gray-900">{current!.name}</span>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                />
              </button>

              {open && (
                <div
                  role="listbox"
                  className={`absolute z-50 mt-2 w-60 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg ${
                    bar ? 'left-0' : 'right-0'
                  }`}
                >
                  <p className="border-b border-gray-100 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    {axis.category.name}
                  </p>
                  {options.map((tag: Tag) => {
                    const active = tag.id === current!.id;
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          setScopeValue(axis.category.id, tag);
                          setOpenAxis(null);
                        }}
                        className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors ${
                          active
                            ? 'bg-primary-50 font-bold text-primary-700'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Check
                          className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'text-primary-600' : 'text-transparent'}`}
                        />
                        <span className="flex-1">{tag.name}</span>
                        <span className="text-xs text-gray-400">{counts[tag.id] ?? 0}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {bar && (
              <span className="text-xs text-gray-500">
                {counts[current!.id] ?? 0} course{(counts[current!.id] ?? 0) === 1 ? '' : 's'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
